import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, Eye, EyeOff, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    platform: 'asb606'
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);

      if (response.data.user.role !== 'admin' && response.data.user.role !== 'staff') {
        toast.error('غير مصرح لك بالدخول');
        return;
      }

      login(response.data.access_token, response.data.user);
      toast.success('تم تسجيل الدخول بنجاح');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'بيانات دخول غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#0a0a0a]" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-md w-full">
        {/* Language Toggle & Back Link */}
        <div className="flex justify-between items-center mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#808080] hover:text-white transition-colors"
          >
            {language === 'ar' ? <ArrowRight className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
            {language === 'ar' ? 'العودة للصفحة الرئيسية' : 'Back to Home'}
          </Link>
          <LanguageToggle />
        </div>

        <div className="text-center mb-8">
          <img 
            src={'/brand/ast-logo-asb606.png'}
            alt="ASB606 Logo"
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="login-title">
            {language === 'ar' ? 'لوحة التحكم' : 'Control Panel'}
          </h1>
          <p className="text-[#808080]">{language === 'ar' ? 'تسجيل دخول الإدارة' : 'Admin Login'}</p>
        </div>

        <div className="bg-[#111111] rounded-2xl border border-[#222222] p-8">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-[#a0a0a0] text-sm">
                {language === 'ar' ? 'اسم المستخدم' : 'Username'}
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#555] focus:border-[#c9a227] focus:ring-[#c9a227] ps-10"
                  placeholder={language === 'ar' ? 'أدخل اسم المستخدم' : 'Enter username'}
                  data-testid="login-username"
                />
                <User className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 text-[#555]`} />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#a0a0a0] text-sm">
                {language === 'ar' ? 'كلمة المرور' : 'Password'}
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#555] focus:border-[#c9a227] focus:ring-[#c9a227] pe-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-[#808080] hover:text-white transition-colors`}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#c9a227] hover:bg-[#d4b53a] text-black font-semibold py-5 text-base transition-all"
              data-testid="login-submit-btn"
            >
              {loading 
                ? (language === 'ar' ? 'جاري تسجيل الدخول...' : 'Logging in...') 
                : (language === 'ar' ? 'تسجيل الدخول' : 'Login')}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-[#222] text-center">
            <p className="text-xs text-[#555]">
              {language === 'ar' ? 'هذه المنطقة مخصصة للإدارة فقط' : 'This area is for administrators only'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
